import { FC, useMemo } from 'react'
import { Alert, AlertTitle } from '@mui/material'
import { ExternalUserCaseRole, FetchBillDetailsQuery } from '../../../../generated'

type Bill = FetchBillDetailsQuery['fetchBillDetails']['bill']

interface MissingClientAddressAlertProps {
  bill?: Bill
}

const hasText = (value?: string | null) => !!value && value.trim().length > 0

/**
 * Warns when the Primary Client on a bill has no postal address.
 *
 * Printing renders the address block from the party's `billingAddress`, falling back to the
 * client's first saved address. With neither, there is nothing to address the copy to — which
 * previously surfaced only at print time, as a failure with no indication of what to fix.
 */
const MissingClientAddressAlert: FC<MissingClientAddressAlertProps> = ({ bill }) => {
  const isMissing = useMemo(() => {
    const parties = bill?.case?.caseToExternalUserToExternalUserRole
    // Nothing loaded yet — stay quiet rather than warning about data we have not seen.
    if (!parties?.length) return false

    const primaryClientRole = parties.find(party => party?.caseRole === ExternalUserCaseRole.PrimaryClient)
    if (!primaryClientRole) return false

    if (hasText(primaryClientRole.billingAddress)) return false

    const addresses = primaryClientRole.externalUserToExternalUserRole?.externalUser?.addresses || []
    // A row can exist with every field blank, so check for actual content rather than length.
    return !addresses.some(
      address =>
        hasText(address?.address1) ||
        hasText(address?.address2) ||
        hasText(address?.city) ||
        hasText(address?.zipCode)
    )
  }, [bill])

  if (!isMissing) return null

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>No client address on file</AlertTitle>
      This bill cannot be printed or mailed until the primary client has an address. Add one on the
      client&apos;s contact record, or set a billing address for the client on this matter.
    </Alert>
  )
}

export default MissingClientAddressAlert
